import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { from, lastValueFrom, Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { enviroment } from 'src/environments/environment';
import { uploadPartsConcurrently } from 'src/app/shared/upload/multipart-parts';
import {
  AvatarUploadResult,
  ModerationCounts,
  ModerationQueueItem,
  SanctionedUsersResponse,
  ProjectMiniResponse,
  ProjectProfileResponse,
  FollowedProjectsPageResponse,
  ProjectSearchHitResponse,
  SocialCommentRequest,
  SocialCommentResponse,
  SocialCreatePostRequest,
  SocialFeedResponse,
  SocialFollowListResponse,
  SocialMediaUploadRequest,
  SocialMediaUploadResponse,
  SocialModerationDecisionRequest,
  SocialPostResponse,
  SocialProfileResponse,
  SocialReportRequest,
  SocialReportResponse,
  SocialReportStatus,
  SocialSearchUserHit,
  SocialUpdatePostRequest,
  UpdateProfileRequest,
} from '../model/social/social.types';

@Injectable({ providedIn: 'root' })
export class SocialService {
  private readonly base = `${enviroment.BASE_URL_API}/user/social`;
  private readonly adminBase = `${enviroment.BASE_URL_API}/user/admin/social`;

  constructor(private http: HttpClient) {}

  private static readonly UPLOAD_PART_SIZE = 10 * 1024 * 1024; // 10 MB
  private readonly filesBase = `${enviroment.BASE_URL_FILES}/files/user`;

  /** Passo 1: registrar mídia no backend → recebe `mediaId` + `objectKey`. */
  uploadMediaRegister(payload: SocialMediaUploadRequest): Observable<SocialMediaUploadResponse> {
    return this.http.post<SocialMediaUploadResponse>(`${this.base}/media/upload`, payload);
  }

  /**
   * Passo 2 + 3 + 4: upload multipart completo ao S3 via API Gateway (folder=social).
   * Idêntico ao fluxo de projetos, trocando `folder` por `social`.
   */
  async uploadFileMultipart(userId: string, objectKey: string, file: File): Promise<void> {
    const fileSegment = objectKey.replace(/^social\//, '');
    const contentType = file.type || 'application/octet-stream';

    const startUrl = `${this.filesBase}/${userId}/upload/start`;
    const startBody = {
      file: fileSegment,
      contentType,
      folder: 'social',
      size: file.size / 1024,
    };
    const startResp = await lastValueFrom(
      this.http.post<{ objectName: string }>(startUrl, startBody, { observe: 'response' })
    );
    const uploadId = startResp.headers.get('Upload-Id');
    if (!uploadId) {
      throw new Error('Upload-Id não retornado pelo servidor.');
    }
    const objectName = startResp.body?.objectName;
    if (!objectName) {
      throw new Error('objectName não retornado pelo servidor.');
    }

    // Mesmo pool de 6 partes em paralelo (com retry) do upload de mapas/logs — antes era 1 a 1.
    const headers = new HttpHeaders({ 'Content-Type': 'application/octet-stream' });
    const parts = await uploadPartsConcurrently(
      file,
      SocialService.UPLOAD_PART_SIZE,
      (partNumber, chunk) => this.http.put<any>(
        `${this.filesBase}/${userId}/upload/${objectName}?uploadId=${uploadId}&partNumber=${partNumber}`,
        chunk,
        { headers, observe: 'response' }
      )
    );

    const completeUrl = `${this.filesBase}/${userId}/upload/complete`;
    await lastValueFrom(
      this.http.post(completeUrl, { objectName, uploadId, parts }, { observe: 'response' })
    );
  }

  createPost(payload: SocialCreatePostRequest): Observable<SocialPostResponse> {
    return this.http.post<SocialPostResponse>(`${this.base}/posts`, payload);
  }

  updatePost(postId: string, payload: SocialUpdatePostRequest): Observable<SocialPostResponse> {
    return this.http.patch<SocialPostResponse>(`${this.base}/posts/${postId}`, payload);
  }

  deletePost(postId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/posts/${postId}`);
  }

  archivePost(postId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/posts/${postId}/archive`, {});
  }

  unarchivePost(postId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/posts/${postId}/unarchive`, {});
  }

  getFeed(pageSize = 20, cursor?: string | null): Observable<SocialFeedResponse> {
    let params = new HttpParams().set('pageSize', String(pageSize));
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.http.get<SocialFeedResponse>(`${this.base}/feed`, { params });
  }

  getExplore(pageSize = 20, cursor?: string | null, sort?: 'recent'): Observable<SocialFeedResponse> {
    let params = new HttpParams().set('pageSize', String(pageSize));
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    if (sort) {
      params = params.set('sort', sort);
    }
    return this.http.get<SocialFeedResponse>(`${this.base}/explore`, { params });
  }

  getPost(postId: string): Observable<SocialPostResponse> {
    return this.http.get<SocialPostResponse>(`${this.base}/posts/${postId}`);
  }

  likePost(postId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/posts/${postId}/likes`, {});
  }

  unlikePost(postId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/posts/${postId}/likes`);
  }

  getComments(postId: string, take = 50): Observable<SocialCommentResponse[]> {
    const params = new HttpParams().set('take', String(take));
    return this.http.get<SocialCommentResponse[]>(`${this.base}/posts/${postId}/comments`, {
      params,
    });
  }

  createComment(postId: string, body: SocialCommentRequest): Observable<SocialCommentResponse> {
    return this.http.post<SocialCommentResponse>(`${this.base}/posts/${postId}/comments`, body);
  }

  updateComment(commentId: string, body: SocialCommentRequest): Observable<SocialCommentResponse> {
    return this.http.patch<SocialCommentResponse>(`${this.base}/comments/${commentId}`, body);
  }

  deleteComment(commentId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/comments/${commentId}`);
  }

  /** Cria reply a um comentário top-level. Backend ancora replies de replies no comentário raiz. */
  replyToComment(parentCommentId: string, body: SocialCommentRequest): Observable<SocialCommentResponse> {
    return this.http.post<SocialCommentResponse>(`${this.base}/comments/${parentCommentId}/replies`, body);
  }

  /** Lista replies de um comentário em ordem cronológica crescente. */
  listReplies(parentCommentId: string, take = 50): Observable<SocialCommentResponse[]> {
    const params = new HttpParams().set('take', String(take));
    return this.http.get<SocialCommentResponse[]>(`${this.base}/comments/${parentCommentId}/replies`, {
      params,
    });
  }

  followUser(userId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/users/${userId}/follow`, {});
  }

  unfollowUser(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/users/${userId}/follow`);
  }

  getProfile(userId: string): Observable<SocialProfileResponse> {
    return this.http.get<SocialProfileResponse>(`${this.base}/users/${userId}/profile`);
  }

  updateProfile(body: UpdateProfileRequest): Observable<SocialProfileResponse> {
    return this.http.patch<SocialProfileResponse>(`${this.base}/profile`, body);
  }

  registerAvatarUpload(fileName: string): Observable<AvatarUploadResult> {
    return this.http.post<AvatarUploadResult>(`${this.base}/profile/avatar`, { file: fileName });
  }

  /**
   * Troca do avatar em um único fluxo: registra a mídia → faz o upload binário ao
   * S3 → persiste no perfil (PATCH só com `avatarMediaId`; bio, website e nickname
   * são preservados pelo backend, que usa o valor existente quando o campo vem
   * nulo). Retorna o perfil atualizado.
   *
   * O modal do perfil social usa os passos separados porque salva avatar + bio +
   * nickname juntos; este método expõe o caminho "só avatar" para outras telas
   * (ex.: o menu do avatar no header) sem duplicar a lógica de multipart.
   */
  changeAvatar(userId: string, file: File): Observable<SocialProfileResponse> {
    return this.registerAvatarUpload(file.name).pipe(
      switchMap((res) =>
        from(this.uploadFileMultipart(userId, res.objectKey, file)).pipe(
          switchMap(() => this.updateProfile({ avatarMediaId: res.mediaId })),
        ),
      ),
    );
  }

  /**
   * Busca global de perfis por texto (autocomplete no feed).
   * `GET user/social/users/search?query=&take=` — backend: default take 10, máx. 50.
   */
  searchUsers(query: string, take = 8): Observable<SocialSearchUserHit[]> {
    const q = query?.trim() ?? '';
    if (!q) {
      return of([]);
    }
    const capped = Math.min(Math.max(take, 1), 50);
    const params = new HttpParams().set('query', q).set('take', String(capped));
    return this.http.get<unknown>(`${this.base}/users/search`, { params }).pipe(
      map((res) => SocialService.parseSearchUsersResponse(res, capped)),
      catchError(() => of([]))
    );
  }

  private static parseSearchUsersResponse(res: unknown, take: number, depth = 0): SocialSearchUserHit[] {
    if (depth > 4) {
      return [];
    }
    let raw: unknown[] = [];
    if (Array.isArray(res)) {
      raw = res;
    } else if (res && typeof res === 'object') {
      const o = res as Record<string, unknown>;
      let list =
        o['users'] ??
        o['Users'] ??
        o['items'] ??
        o['Items'] ??
        o['results'] ??
        o['Results'] ??
        o['list'] ??
        o['List'] ??
        o['value'] ??
        o['Value'];
      if (!Array.isArray(list) && list && typeof list === 'object') {
        const inner = list as Record<string, unknown>;
        list =
          inner['items'] ??
          inner['Items'] ??
          inner['users'] ??
          inner['Users'] ??
          inner['results'] ??
          inner['Results'];
      }
      if (!Array.isArray(list)) {
        const nested = (o['data'] ?? o['Data']) as unknown;
        if (nested != null && nested !== res) {
          return SocialService.parseSearchUsersResponse(nested, take, depth + 1);
        }
      }
      raw = Array.isArray(list) ? list : [];
    }
    const mapped = raw.map((u) => SocialService.normalizeSearchUserHit(u)).filter((h) => h.userId);
    return mapped.slice(0, take);
  }

  private static normalizeSearchUserHit(u: unknown): SocialSearchUserHit {
    if (!u || typeof u !== 'object') {
      return { userId: '' };
    }
    const o = u as Record<string, unknown>;
    const userId = String(o['userId'] ?? o['UserId'] ?? o['id'] ?? o['Id'] ?? '');
    const ifbv = o['isFollowedByViewer'] ?? o['IsFollowedByViewer'];
    const hit: SocialSearchUserHit = {
      userId,
      nickname: (o['nickname'] ?? o['Nickname']) as string | null | undefined,
      name: (o['name'] ?? o['Name'] ?? o['fullName'] ?? o['FullName']) as string | null | undefined,
    };
    if (ifbv !== undefined && ifbv !== null) {
      hit.isFollowedByViewer = Boolean(ifbv);
    }
    return hit;
  }

  getUserPosts(userId: string, pageSize = 20, cursor?: string | null): Observable<SocialFeedResponse> {
    let params = new HttpParams().set('pageSize', String(pageSize));
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.http.get<SocialFeedResponse>(`${this.base}/users/${userId}/posts`, { params });
  }

  getFollowers(userId: string, page = 1, pageSize = 20): Observable<SocialFollowListResponse> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    return this.http.get<SocialFollowListResponse>(`${this.base}/users/${userId}/followers`, {
      params,
    });
  }

  getFollowing(userId: string, page = 1, pageSize = 20): Observable<SocialFollowListResponse> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    return this.http.get<SocialFollowListResponse>(`${this.base}/users/${userId}/following`, {
      params,
    });
  }

  // ===== Bloqueio (simétrico: nenhum dos dois vê mais o outro no feed) =====

  /** `POST users/{userId}/block` — idempotente; 400 ao bloquear a si mesmo; 404 alvo inexistente. */
  blockUser(userId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/users/${userId}/block`, {});
  }

  /** `DELETE users/{userId}/block` — idempotente. Não refaz follows desfeitos no bloqueio. */
  unblockUser(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/users/${userId}/block`);
  }

  /** `GET me/blocked-users` — pessoas que o viewer bloqueou (lista do próprio perfil). */
  getBlockedUsers(page = 1, pageSize = 50): Observable<SocialFollowListResponse> {
    const params = new HttpParams().set('page', String(page)).set('pageSize', String(pageSize));
    return this.http.get<SocialFollowListResponse>(`${this.base}/me/blocked-users`, { params });
  }

  // ===== Projeto-cêntrico (feed unificado) =====

  /** Perfil completo do projeto. Retorna 404 se privado e viewer não é dono. */
  getProjectProfile(projectId: string): Observable<ProjectProfileResponse> {
    return this.http.get<ProjectProfileResponse>(`${this.base}/projects/${projectId}`);
  }

  /** Feed paginado de posts de um projeto específico. */
  getProjectPosts(
    projectId: string,
    pageSize = 20,
    cursor?: string | null
  ): Observable<SocialFeedResponse> {
    let params = new HttpParams().set('pageSize', String(pageSize));
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.http.get<SocialFeedResponse>(
      `${this.base}/projects/${projectId}/posts`,
      { params }
    );
  }

  /** Lista usuários que seguem um projeto. */
  getProjectFollowers(
    projectId: string,
    page = 1,
    pageSize = 100
  ): Observable<SocialFollowListResponse> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    return this.http.get<SocialFollowListResponse>(
      `${this.base}/projects/${projectId}/followers`,
      { params }
    );
  }

  /** Seguir projeto (idempotente — UPSERT no backend). */
  followProject(projectId: string): Observable<void> {
    return this.http.post<void>(
      `${this.base}/projects/${projectId}/follow`,
      {}
    );
  }

  /** Deixar de seguir projeto (idempotente). */
  unfollowProject(projectId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/projects/${projectId}/follow`
    );
  }

  /** Busca de projetos públicos (autocomplete). */
  searchProjects(query: string, limit = 10): Observable<ProjectSearchHitResponse[]> {
    const q = query?.trim() ?? '';
    if (!q) {
      return of([]);
    }
    const capped = Math.min(Math.max(limit, 1), 50);
    const params = new HttpParams().set('q', q).set('limit', String(capped));
    return this.http
      .get<ProjectSearchHitResponse[]>(`${this.base}/projects/search`, { params })
      .pipe(catchError(() => of([])));
  }

  /**
   * `GET me/followed-projects/paged` — projetos que o viewer segue, paginados e com o total
   * (lista "Projetos que sigo" do perfil). O estado do botão "Seguir" dos cards NÃO vem daqui:
   * cada post já traz `viewerFollowsProject`.
   */
  getMyFollowedProjectsPage(page = 1, pageSize = 20): Observable<FollowedProjectsPageResponse> {
    const params = new HttpParams().set('page', String(page)).set('pageSize', String(pageSize));
    return this.http.get<FollowedProjectsPageResponse>(`${this.base}/me/followed-projects/paged`, { params });
  }

  /**
   * Projetos públicos do próprio viewer (popular o seletor "publicar em qual").
   * Atenção: rota fora do prefixo /user/social/.
   */
  getMyPublicProjects(limit = 50): Observable<ProjectMiniResponse[]> {
    const params = new HttpParams().set('limit', String(limit));
    return this.http.get<ProjectMiniResponse[]>(
      `${enviroment.BASE_URL_API}/user/me/projects/public`,
      { params }
    );
  }

  reportContent(payload: SocialReportRequest): Observable<SocialReportResponse> {
    return this.http.post<SocialReportResponse>(`${this.base}/reports`, payload);
  }

  /** Fila de moderação agrupada por alvo, filtrada por status (`pending` por padrão). */
  getModerationQueue(
    status: SocialReportStatus = 'pending',
    page = 1,
    pageSize = 50,
    reasonKey?: string | null
  ): Observable<ModerationQueueItem[]> {
    let params = new HttpParams()
      .set('status', status)
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    if (reasonKey) params = params.set('reason', reasonKey);
    return this.http.get<ModerationQueueItem[]>(`${this.adminBase}/moderation/queue`, { params });
  }

  /** Contagem de denúncias por status (badges das abas). */
  getModerationCounts(): Observable<ModerationCounts> {
    return this.http.get<ModerationCounts>(`${this.adminBase}/moderation/counts`);
  }

  /** Aplica a decisão ao grupo do `representativeReportId` (204 no sucesso). */
  postModerationDecision(
    reportId: string,
    payload: SocialModerationDecisionRequest
  ): Observable<void> {
    return this.http.post<void>(`${this.adminBase}/moderation/${reportId}/decision`, payload);
  }

  /** Contas suspensas ou banidas do feed, com busca por nickname/nome (abas Suspensas/Banidas do admin). */
  getSanctionedUsers(
    status: 'suspended' | 'banned',
    query?: string | null,
    page = 1,
    pageSize = 50
  ): Observable<SanctionedUsersResponse> {
    let params = new HttpParams().set('status', status).set('page', String(page)).set('pageSize', String(pageSize));
    const q = query?.trim();
    if (q) params = params.set('q', q);
    return this.http.get<SanctionedUsersResponse>(`${this.adminBase}/sanctions`, { params });
  }

  /** Reativa no feed social um usuário suspenso/banido pela moderação (204 no sucesso; 409 se não estava sancionado). */
  reinstateUser(userId: string): Observable<void> {
    return this.http.post<void>(`${this.adminBase}/users/${userId}/reinstate`, {});
  }

  sponsorPost(postId: string, sponsored: boolean): Observable<void> {
    const params = new HttpParams().set('sponsored', String(sponsored));
    return this.http.post<void>(`${this.adminBase}/posts/${postId}/sponsor`, {}, { params });
  }

  static parseHttpErrorMessage(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return 'Erro inesperado. Tente novamente.';
    }
    const e = error.error;
    if (typeof e === 'string' && e.trim()) {
      return e;
    }
    if (e && typeof e === 'object') {
      const obj = e as {
        errors?: { Error?: unknown[] };
        message?: unknown;
        error?: unknown;
      };
      // 400 DomainException: { errors: { Error: ["msg1", ...] } }
      const domainErr = obj.errors?.Error?.[0];
      if (typeof domainErr === 'string' && domainErr.trim()) {
        return domainErr;
      }
      // { message } no topo (validação 400 simples ou 500 { message, code })
      if (typeof obj.message === 'string' && obj.message.trim()) {
        return obj.message;
      }
      // Aninhado: { error: "str" } ou { error: { message } }
      if ('error' in obj) {
        const inner = obj.error;
        if (typeof inner === 'string') {
          return inner;
        }
        if (inner && typeof inner === 'object' && 'message' in inner) {
          return String((inner as { message?: string }).message);
        }
      }
    }
    if (error.status === 401) {
      return 'Sessão expirada ou não autorizado.';
    }
    if (error.status === 403) {
      return 'Acesso negado.';
    }
    if (error.status === 404) {
      return 'Recurso não encontrado.';
    }
    if (error.status === 409) {
      return 'Conflito ao processar a solicitação.';
    }
    return error.message || 'Erro de comunicação com o servidor.';
  }
}
